/**
 * A FOTO DA COMPROVAÇÃO (pré-vencido, paradão, Giro, recontagem): reduzida para no máximo 1600px
 * e carimbada com data, hora e quem tirou — a marca d'água vai GRAVADA nos pixels, não num campo
 * que dá para editar depois. Sai sempre JPEG (o servidor confere a assinatura do arquivo).
 *
 * A fonte normal é o quadro da CÂMERA DO INOVAPAY (`CameraDaFoto`, já ≤ 1600px). O arquivo (Blob)
 * só chega quando o aparelho não abre a câmera no navegador e a pessoa usou a câmera do celular.
 */

const LADO_MAXIMO = 1600;

/** A foto: o quadro capturado pela câmera do InovaPay ou o arquivo da câmera do celular. */
export type FonteDaFoto = Blob | HTMLCanvasElement;

/** Largura × altura cabendo em LADO_MAXIMO, sem aumentar foto pequena. */
export function medidaReduzida(largura: number, altura: number) {
  const escala = Math.min(1, LADO_MAXIMO / Math.max(largura, altura));
  return { w: Math.round(largura * escala), h: Math.round(altura * escala) };
}

export async function carimbarFoto(fonte: FonteDaFoto, linhas: readonly string[]): Promise<Blob> {
  // Arquivo da câmera do celular (caminho raro): precisa abrir a foto inteira — é pesado em
  // celular fraco, por isso a câmera do InovaPay é o caminho normal.
  const imagem = fonte instanceof Blob ? await createImageBitmap(fonte) : fonte;
  const { w, h } = medidaReduzida(imagem.width, imagem.height);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('Canvas indisponível.');
  g.drawImage(imagem, 0, 0, w, h);
  if (imagem instanceof ImageBitmap) imagem.close();

  const fonteDoTexto = Math.max(14, Math.round(Math.min(w, h) * 0.034));
  const altura = Math.round(fonteDoTexto * 1.45 * linhas.length + fonteDoTexto * 0.9);
  g.fillStyle = 'rgba(17, 19, 21, 0.62)';
  g.fillRect(0, h - altura, w, altura);
  g.fillStyle = '#ffffff';
  g.textBaseline = 'top';
  linhas.forEach((linha, i) => {
    g.font = `${i === 0 ? '600 ' : ''}${String(fonteDoTexto)}px "IBM Plex Sans", system-ui, sans-serif`;
    g.fillText(
      linha,
      Math.round(fonteDoTexto * 0.7),
      h - altura + Math.round(fonteDoTexto * 0.45) + i * fonteDoTexto * 1.45,
      w - fonteDoTexto * 1.4,
    );
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        // Solta os pixels já: celular fraco segura pouco canvas grande ao mesmo tempo.
        canvas.width = 0;
        canvas.height = 0;
        if (b) resolve(b);
        else reject(new Error('Falha ao gerar a foto.'));
      },
      'image/jpeg',
      0.85,
    );
  });
}

/** '24/09/2026 09:14' no horário de Brasília. */
export function agoraCarimbo(agora = new Date()): string {
  const d = agora.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  const t = agora.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
  return `${d} ${t}`;
}
